import { defineStore } from 'pinia'
import { planAllows } from '~/composables/usePlanFeature'
import type { ChatScopeWire } from '#shared/web-chat/scope'
import type { Conversation, ConversationSummary } from '~~/shared/chat/conversation'

// Re-exported: 3 client files import this name from the store. The DECLARATION lives in `shared/`
// so `listConversations` can be annotated with it.
export type { ConversationSummary }


export const useChatsStore = defineStore('chats', () => {
  const { apiFetch } = useApi()

  const conversations = ref<ConversationSummary[]>([])
  const fetchedAt = ref(0)
  const loading = ref(false)

  const STALE_MS = 30_000

  async function listConversations(params?: { search?: string; favorite?: boolean }): Promise<ConversationSummary[]> {
    const query: Record<string, string> = {}
    if (params?.search) query.search = params.search
    if (params?.favorite) query.favorite = 'true'
    const qs = new URLSearchParams(query).toString()
    const data = await apiFetch<ConversationSummary[]>(`/api/conversations${qs ? `?${qs}` : ''}`)
    if (!params?.search && !params?.favorite) {
      conversations.value = data
      fetchedAt.value = Date.now()
    }
    return data
  }

  async function fetchIfStale() {
    // The shell's warm-up. Without a seat, or on a plan without chat, the server refuses the list
    // (`no_seat` / `plan_feature`, MAR-1212), so it is not asked: the profile already says so.
    if (!(await planAllows('chat'))) return
    const isFresh = fetchedAt.value > 0 && (Date.now() - fetchedAt.value) < STALE_MS
    if (isFresh && conversations.value.length > 0) {
      listConversations()
      return
    }
    await listConversations()
  }

  /**
   * `jurisdiction` is the composer's pick (MAR-1418), sent only when the user made one — `null` lets
   * the chat run on the org's main. Resolves with the id AND the jurisdiction the row actually holds:
   * the server stores `null` for a pick the org no longer offers, and the caller must adopt that, not
   * what it sent (MAR-1418 F2).
   */
  async function createConversation(
    firstMessage: string,
    scope?: ChatScopeWire | null,
    jurisdiction?: string | null,
  ): Promise<{ id: string, jurisdiction: string | null }> {
    const title = firstMessage.slice(0, 80) + (firstMessage.length > 80 ? '...' : '')
    const caseId = scope?.caseId ?? null
    const clientId = scope?.clientId ?? null
    const conv = await apiFetch<Conversation>('/api/conversations', {
      method: 'POST',
      body: { title, caseId, clientId, ...(jurisdiction ? { jurisdiction } : {}) },
    })
    const now = new Date().toISOString()
    conversations.value = [
      // Filed under the case it starts in, for good (MAR-1468): `setConversationScope` below moves
      // `case_id`, never `filed_case_id`. The server row says the same (`createConversation`).
      { id: conv.id, title, is_favorite: false, message_count: 1, created_at: now, updated_at: now, case_id: caseId, client_id: clientId, filed_case_id: conv.filed_case_id ?? caseId },
      ...conversations.value,
    ]
    return { id: conv.id, jurisdiction: conv.jurisdiction ?? null }
  }

  async function deleteConversation(id: string) {
    await apiFetch(`/api/conversations/${id}`, { method: 'DELETE' })
    conversations.value = conversations.value.filter(c => c.id !== id)
  }

  /** Delete several conversations. Returns the ids that were successfully removed. */
  async function deleteConversations(ids: string[]): Promise<string[]> {
    const results = await Promise.allSettled(
      ids.map(id => apiFetch(`/api/conversations/${id}`, { method: 'DELETE' })),
    )
    const deleted = ids.filter((_, i) => results[i]!.status === 'fulfilled')
    const deletedSet = new Set(deleted)
    conversations.value = conversations.value.filter(c => !deletedSet.has(c.id))
    return deleted
  }

  async function renameConversation(id: string, title: string) {
    await apiFetch(`/api/conversations/${id}`, {
      method: 'PATCH',
      body: { title },
    })
    const conv = conversations.value.find(c => c.id === id)
    if (conv) conv.title = title
  }

  /**
   * Persist the chat's scope rung (§6b). Both ids always ride along so the server can clear the other.
   * The scope only changes what the chat searches: it never touches `filed_case_id`, so the
   * conversation stays on the list of the case it was started from (MAR-1468).
   */
  async function setConversationScope(id: string, scope: ChatScopeWire) {
    await apiFetch(`/api/conversations/${id}`, {
      method: 'PATCH',
      body: { caseId: scope.caseId, clientId: scope.clientId },
    })
    const conv = conversations.value.find(c => c.id === id)
    if (conv) {
      conv.case_id = scope.caseId
      conv.client_id = scope.clientId
    }
  }

  /** Persist the chat's jurisdiction (MAR-1418); `null` = back to the org default. */
  async function setConversationJurisdiction(id: string, jurisdiction: string | null) {
    await apiFetch(`/api/conversations/${id}`, {
      method: 'PATCH',
      body: { jurisdiction },
    })
  }

  async function toggleFavorite(conv: ConversationSummary) {
    const newVal = !conv.is_favorite
    conv.is_favorite = newVal
    try {
      await apiFetch(`/api/conversations/${conv.id}/favorite`, {
        method: 'PATCH',
        body: { is_favorite: newVal },
      })
    } catch {
      conv.is_favorite = !newVal
    }
  }

  return {
    conversations,
    fetchedAt,
    loading,
    listConversations,
    fetchIfStale,
    createConversation,
    deleteConversation,
    deleteConversations,
    renameConversation,
    setConversationScope,
    setConversationJurisdiction,
    toggleFavorite,
  }
})
